/**
 * 토큰 확보 (WORK&Hub SSO)
 * ---------------------------------------------------------------------------
 * CRM 에는 더 이상 로그인 화면이 없다. 플랫폼에서 로그인하고, CRM 은 그 세션으로
 * **자기 몫의 토큰**을 받아 온다. (WORK&ECO 와 같은 방식)
 *
 * 흐름
 *   1. 주소에 #sso_code 가 있으면 → 교환해서 토큰과 제품 전용 쿠키를 받는다
 *   2. 없으면 → 제품 전용 쿠키로 갱신을 시도한다
 *   3. 그것도 없으면 → 플랫폼의 /sso 로 보낸다 (돌아올 주소를 함께)
 *
 * ## 왜 플랫폼의 refresh 쿠키를 직접 쓰지 않는가
 * 그 토큰은 1회용으로 회전한다. 플랫폼 콘솔 탭과 CRM 탭이 같은 쿠키를 공유하면
 * 나중에 갱신하는 쪽이 이미 쓴 토큰을 내밀게 되고, 플랫폼은 그것을 탈취로 보고
 * **그 사용자의 세션을 전부 끊는다.** 두 사이트를 같이 열면 반드시 일어난다.
 * 그래서 제품마다 자기 쿠키(`wnh_rt_crm`)를 따로 받는다 — 체인이 갈라져 있어
 * 서로를 죽이지 않고, 재사용 탐지는 체인별로 그대로 살아 있다.
 *
 * ## 액세스 토큰을 localStorage 에 두지 않는다
 * 예전 CRM 은 `localStorage.getItem('token')` 으로 들고 다녔다. 그러면 XSS 한
 * 번에 토큰이 통째로 나간다. 여기서는 **메모리에만** 둔다. 새로고침하면 사라지지만
 * 제품 전용 쿠키로 곧바로 다시 받아오므로 사용자는 차이를 못 느낀다.
 */

const PLATFORM_API = import.meta.env.VITE_HUB_API_BASE_URL || 'http://localhost:3000/api';
const PLATFORM_WEB = import.meta.env.VITE_HUB_WEB_URL || 'http://localhost:5273';
const PRODUCT_CODE = import.meta.env.VITE_PRODUCT_CODE || 'crm';

let accessToken = null;
let expiresAt = 0;
let inflight = null;

/** 만료 직전에 미리 갱신한다. 요청 도중에 만료되면 그 요청만 실패한다. */
const RENEW_MARGIN_MS = 30_000;

export function getAccessToken() {
  return accessToken;
}

export function clearAccessToken() {
  accessToken = null;
  expiresAt = 0;
}

function store(body) {
  if (!body?.accessToken) {
    clearAccessToken();
    return null;
  }
  accessToken = body.accessToken;
  expiresAt = Date.now() + (body.expiresIn ?? 900) * 1000;
  return accessToken;
}

/**
 * 주소에 실려 온 1회용 코드를 꺼낸다.
 *
 * 프래그먼트(#)를 쓴다. 쿼리로 넘기면 서버 로그와 Referer 에 코드가 남는다.
 * 꺼낸 뒤에는 주소에서 지운다 — 새로고침하면 이미 쓴 코드로 다시 시도하게 된다.
 */
function takeSsoCode() {
  const hash = window.location.hash.replace(/^#/, '');
  if (!hash) return null;

  const params = new URLSearchParams(hash);
  const code = params.get('sso_code');
  if (!code) return null;

  params.delete('sso_code');
  const rest = params.toString();
  window.history.replaceState(
    null,
    '',
    `${window.location.pathname}${window.location.search}${rest ? `#${rest}` : ''}`,
  );

  return code;
}

/**
 * 유효한 액세스 토큰을 돌려준다. 없으면 받아 온다.
 * 끝내 못 받으면 null — 호출부가 로그인으로 보낸다.
 */
export async function ensureAccessToken() {
  if (accessToken && Date.now() < expiresAt - RENEW_MARGIN_MS) return accessToken;
  return acquire();
}

/** 401 을 받은 직후처럼 캐시를 믿을 수 없을 때 강제로 다시 받는다. */
export async function forceRefresh() {
  clearAccessToken();
  return acquire();
}

function acquire() {
  // 화면 하나가 요청을 여러 개 던지므로, 동시에 들어와도 한 번만 받아 온다.
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      // 1) 플랫폼이 막 건네준 코드가 있으면 그것을 먼저 쓴다.
      const code = takeSsoCode();
      if (code) {
        const res = await fetch(`${PLATFORM_API}/auth/sso/exchange`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ code, productCode: PRODUCT_CODE }),
        });
        if (res.ok) return store(await res.json());
        // 코드가 만료·중복 사용된 경우다. 아래 갱신으로 넘어간다.
      }

      // 2) 제품 전용 쿠키로 갱신.
      const res = await fetch(`${PLATFORM_API}/auth/sso/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ productCode: PRODUCT_CODE }),
      });

      if (res.ok) return store(await res.json());

      clearAccessToken();
      return null;
    } catch {
      /*
       * 플랫폼이 꺼져 있거나 네트워크가 끊긴 경우. 로그인으로 보내면 거기서도
       * 같은 이유로 실패해 화면이 왔다 갔다 한다. 호출부가 안내를 맡는다.
       */
      clearAccessToken();
      return null;
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}

/* ---------------------------------------------------------------------------
 * 왕복 루프 차단기
 * ---------------------------------------------------------------------------
 * CRM 이 플랫폼으로 보내고 → 플랫폼에 세션이 있어 곧바로 코드를 주고 → CRM 이
 * 또 거절당해 다시 보내는 상황이 실제로 일어났다. 사람 눈에는 화면이 깜빡이는
 * 것으로 보이지만, 서버에서 보면 **초당 수십 번**의 로그인 요청이다. 즉 우리
 * 스스로 플랫폼을 때리는 꼴이 된다.
 *
 * 그래서 짧은 시간 안에 같은 왕복이 거듭되면 더 보내지 않고 멈춘다.
 * sessionStorage 를 쓰는 이유 — 페이지가 통째로 넘어가므로 메모리 변수는 매번
 * 초기화된다. 탭을 닫으면 사라지므로 사용자가 영영 갇히지도 않는다.
 * ------------------------------------------------------------------------ */

const LOOP_KEY = 'workncrm.ssoBounces';
/** 이 시간(ms) 안에 일어난 왕복만 센다. */
const LOOP_WINDOW_MS = 10_000;
/** 이 횟수를 넘으면 멈춘다. 정상적인 경우 1번이면 끝난다. */
const LOOP_LIMIT = 3;

function readBounces() {
  try {
    const raw = sessionStorage.getItem(LOOP_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw);
    return Array.isArray(list) ? list.filter((t) => Number.isFinite(t)) : [];
  } catch {
    return [];
  }
}

/** 왕복을 한 번 기록하고, 한도를 넘었는지 알려준다. */
function recordBounce() {
  const now = Date.now();
  const recent = readBounces().filter((t) => now - t < LOOP_WINDOW_MS);
  recent.push(now);

  try {
    sessionStorage.setItem(LOOP_KEY, JSON.stringify(recent));
  } catch {
    // 저장이 막혀도 이동 자체는 막지 않는다. 차단기만 동작하지 않을 뿐이다.
  }

  return recent.length > LOOP_LIMIT;
}

/** 로그인에 성공했으면 센 것을 지운다. 다음에 다시 깨끗하게 세기 위해서다. */
export function clearBounceCounter() {
  try {
    sessionStorage.removeItem(LOOP_KEY);
  } catch {
    // 지우지 못해도 LOOP_WINDOW_MS 가 지나면 저절로 풀린다.
  }
}

/**
 * 플랫폼으로 보낸다. 로그인이 되어 있으면 바로 코드를 받아 돌아온다.
 *
 * 돌아올 주소에 프래그먼트를 붙이지 않는다. 지금 주소에 이미 쓴 코드가 남아
 * 있을 수 있고, 그것을 그대로 들고 가면 루프가 된다.
 *
 * @returns {boolean} 실제로 보냈으면 true, 루프로 판단해 멈췄으면 false.
 *   false 를 받은 호출부는 사용자에게 안내 화면을 보여 줘야 한다.
 */
export function goToPlatformLogin() {
  if (recordBounce()) {
    console.error(
      'WORK&Hub 와 CRM 사이를 반복해서 오갔습니다. 더 보내지 않고 멈춥니다. ' +
        '보통 이 계정이 CRM 을 쓸 수 없는 경우입니다.',
    );
    return false;
  }

  const back = `${window.location.origin}${window.location.pathname}${window.location.search}`;
  window.location.replace(
    `${PLATFORM_WEB}/sso?product=${encodeURIComponent(PRODUCT_CODE)}` +
      `&next=${encodeURIComponent(back)}`,
  );
  return true;
}

/**
 * 로그아웃.
 *
 * <b>제품 쿠키만 지우면 로그아웃이 되지 않는다.</b> 플랫폼 세션이 살아 있으면
 * CRM 에 다시 들어오는 순간 조용히 새 토큰을 받아 그대로 로그인된다.
 * 사용자가 보기에는 "눌러도 안 나가진다" 가 된다.
 *
 * 그래서 두 가지를 모두 한다.
 *   1. 제품 전용 쿠키(wnh_rt_crm) 폐기
 *   2. 플랫폼 세션 종료 — 로그인 상태 자체를 끝낸다
 *
 * 2번은 같은 세션을 쓰는 다른 제품에서도 로그아웃된다는 뜻이다. 그것이 맞다 —
 * 로그아웃을 누른 사람은 "이 기기에서 나간다" 를 기대하지, "CRM 에서만 나가고
 * 다른 창에서는 계속 로그인" 을 기대하지 않는다. 공용 PC 에서는 그 차이가
 * 그대로 사고가 된다.
 */
export async function signOut() {
  // 세션을 끝내려면 토큰이 필요하다. 지우기 전에 들고 있는다.
  const token = accessToken;
  clearAccessToken();
  clearBounceCounter();

  // 1) 제품 전용 쿠키부터 지운다.
  try {
    await fetch(`${PLATFORM_API}/auth/sso/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ productCode: PRODUCT_CODE }),
    });
  } catch {
    // 쿠키를 못 지워도 아래 세션 종료와 화면 이동은 계속한다.
  }

  // 2) 플랫폼 세션 종료. 이것이 빠지면 로그아웃이 되지 않는다.
  if (token) {
    try {
      await fetch(`${PLATFORM_API}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'content-type': 'application/json',
          authorization: `Bearer ${token}`,
        },
      });
    } catch {
      // 플랫폼에 닿지 못해도 이 기기의 토큰은 이미 지웠다. 화면은 넘어간다.
    }
  }

  window.location.replace(PLATFORM_WEB);
}

export const platformWebUrl = PLATFORM_WEB;
